"use client";

/* ─── Reusable sub-components ──────────────────────────────────────────────── */

function SectionHeading({ n, title }: { n: string; title: string }) {
  return (
    <h2 className="mt-10 mb-4 text-[0.95rem] font-bold tracking-wide text-[#0D1B3E] uppercase">
      {n}. {title}
    </h2>
  );
}

function SubHeading({ id, title }: { id: string; title: string }) {
  return (
    <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">
      {id} {title}
    </h3>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 rounded-md border-l-[3px] border-[#C95A0E] bg-[#FDF8F4] px-4 py-3">
      <p className="m-0 text-[0.72rem] leading-relaxed text-[#5A3E2B] italic">
        {children}
      </p>
    </div>
  );
}

function StepBox({ num, title, children }: { num: string; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 mt-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0D1B3E] text-[0.6rem] font-bold text-white">
        {num}
      </span>
      <div>
        <p className="font-semibold text-[#0D1B3E]">{title}</p>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}

/* ─── Page ─────────────────────────────────────────────────────────────────── */

export default function RefundPolicyPage() {
  return (
    <main className="bg-white font-['DM_Sans',sans-serif] text-[#333]">
      {/* ── Hero ── */}
      <section className="bg-[#0D1B3E] py-16 text-center text-white">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-[0.6rem] font-bold uppercase tracking-[0.25em] text-[#C95A0E]">
            Legal
          </p>
          <h1 className="mt-2 font-['Cormorant_Garamond',Georgia,serif] text-[1.75rem] font-semibold leading-tight sm:text-[2.25rem]">
            Refund &amp; Cancellation Policy
          </h1>
          <p className="mt-2 text-[0.72rem] text-white/50">
            Effective: April 2024
          </p>
          <p className="mt-1 text-[0.65rem] text-white/40">
            Applies to: All Services &bull; All Payment Modes &bull; All Client Segments
          </p>
        </div>
      </section>

      {/* ── Key badges ── */}
      <div className="mx-auto -mt-6 grid max-w-3xl grid-cols-1 gap-3 px-6 sm:grid-cols-3">
        {[
          { label: "Refund Window", value: "Within 30 days of payment" },
          { label: "Processing Time", value: "7–10 business days after approval" },
          { label: "Alternative", value: "Service Credits (12-month validity)" },
        ].map((b) => (
          <div
            key={b.label}
            className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center shadow-sm"
          >
            <p className="text-[0.6rem] font-bold uppercase tracking-[0.15em] text-[#C95A0E]">
              {b.label}
            </p>
            <p className="mt-1 text-[0.7rem] font-medium leading-snug text-[#0D1B3E]">
              {b.value}
            </p>
          </div>
        ))}
      </div>

      {/* ── Body ── */}
      <article className="prose-none mx-auto max-w-3xl px-6 pb-20 pt-10 text-[0.75rem] leading-[1.85] text-[#444]">

        {/* 1 ─ Introduction & Purpose */}
        <SectionHeading n="1" title="Introduction & Purpose" />
        <p>
          We value every client relationship we build. This Refund &amp; Cancellation Policy is designed to bring clarity and fairness to the way we handle situations where a service engagement needs to change or be reconsidered.
        </p>
        <p>
          Our work spans across consultancy, funding facilitation, certifications, legal services, marketing, and technology — each of which involves dedicated time, expertise, and coordinated effort from our team. This policy reflects the reality of how professional service delivery works, while ensuring that every client has a clear, transparent understanding of what to expect.
        </p>
        <p>
          We approach refunds not as an adversarial process, but as a structured review aimed at being fair to both parties. Our goal is to resolve concerns promptly, professionally, and in good faith.
        </p>
        <Callout>
          This policy is governed by the Indian Contract Act, 1872, the Information Technology Act, 2000, and the Digital Personal Data Protection Act, 2023. By engaging our services, you acknowledge and agree to the terms described herein.
        </Callout>

        {/* 2 ─ Scope */}
        <SectionHeading n="2" title="Scope of This Policy" />
        <p>This policy applies to all services offered by the company, including but not limited to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Business Consultancy</strong> — strategy, advisory, 360° business support</li>
          <li><strong>Funding &amp; Financial Services</strong> — loan facilitation, NBFC coordination, government scheme assistance (Mudra, PMEGP, grants, subsidies)</li>
          <li><strong>Certifications &amp; Registrations</strong> — ISO, Startup India, Udyam, GeM, 80 IAC, FCRA, 12A &amp; 80G</li>
          <li><strong>Legal Services</strong> — contracts, compliance documentation, corporate governance support</li>
          <li><strong>Marketing Services</strong> — branding, digital campaigns, performance marketing</li>
          <li><strong>IT &amp; Technology Services</strong> — software development, web &amp; app development, cloud services, CRM/ERP, AI/ML, cybersecurity, and DevOps</li>
        </ul>

        <p className="mt-4">This policy covers all payment modes, including:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Bank transfers (NEFT / RTGS / IMPS)</li>
          <li>UPI and mobile payment platforms</li>
          <li>Credit and debit card payments</li>
          <li>Payment gateways and online portals</li>
          <li>Cheque and demand draft</li>
        </ul>

        {/* 3 ─ Service Commencement */}
        <SectionHeading n="3" title="When Does a Service Begin? (Service Commencement Definition)" />
        <p>
          Understanding when a service is considered to have commenced is important because it determines the eligibility and extent of any potential refund.
        </p>
        <p>A service is considered to have formally commenced from the earliest occurrence of any of the following:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>An initial consultation, scoping call, or discovery meeting has been conducted</li>
          <li>A service agreement, proposal, or work order has been signed or accepted by the client</li>
          <li>Any document collection, review, or preparation has begun on behalf of the client</li>
          <li>An application, form, or filing has been initiated with any government body, authority, or third party</li>
          <li>Internal team resources have been allocated — including project managers, consultants, legal advisors, developers, or marketing specialists</li>
          <li>Any research, strategy, planning, design, or technical work has been undertaken</li>
          <li>A payment has been received and the corresponding work has been scheduled or initiated</li>
        </ul>
        <Callout>
          Even if a deliverable has not yet been handed over to the client, the service is considered commenced once any of the above actions have taken place. This is important because significant effort and expertise are invested from the very start of an engagement.
        </Callout>

        {/* 4 ─ When Is a Refund Considered? */}
        <SectionHeading n="4" title="When Is a Refund Considered?" />
        <p>We understand that circumstances can change. A refund request will be reviewed and considered in the following specific situations:</p>

        <SubHeading id="4.1" title="Service Not Initiated Due to Our Delay" />
        <p>
          If a service has not commenced within a reasonable timeframe solely due to internal delays on our part — and the client has not been informed of an expected timeline extension — the client may raise a refund request. We will evaluate the situation and, if confirmed, process a refund after deducting any administrative and processing charges already incurred.
        </p>

        <SubHeading id="4.2" title="Significant Service Deficiency" />
        <p>
          If the service delivered falls materially short of the agreed scope and we have been given a fair opportunity to correct or remedy the issue within a reasonable timeframe but have been unable to do so, the client may be eligible for a partial refund. This refund will be calculated based on the portion of work not completed, after accounting for effort already invested.
        </p>

        <SubHeading id="4.3" title="Duplicate or Excess Payment" />
        <p>
          In cases where a duplicate payment or excess amount has been paid due to a technical error or payment gateway issue, the extra amount will be refunded in full after verification. We request that such cases be reported within 7 days of the payment date.
        </p>

        <SubHeading id="4.4" title="Service Cancellation Before Commencement" />
        <p>
          If a client cancels a service request before any work has commenced and before any resources have been allocated, a refund may be considered after deducting a nominal processing and administrative fee. Eligibility will be reviewed on a case-by-case basis.
        </p>

        {/* 5 ─ When Refunds Are Not Applicable */}
        <SectionHeading n="5" title="When Refunds Are Not Applicable" />
        <p>
          To ensure fairness and transparency, we want to be upfront about situations where refunds are not applicable. These limitations exist not as restrictions, but because work has already been performed, costs have been incurred, or the outcome depended on factors beyond our control.
        </p>

        <SubHeading id="5.1" title="Client-Side Delays or Non-Cooperation" />
        <p>
          If the service could not be completed or was delayed because the client did not provide required documents, information, approvals, or responses in a timely manner, no refund will be applicable. The responsibility for timely cooperation rests with the client.
        </p>

        <SubHeading id="5.2" title="Incomplete or Incorrect Information Provided by the Client" />
        <p>
          If inaccurate, incomplete, or misleading information was provided by the client and this resulted in rejection, delays, or additional costs, no refund will be issued. Our work is based on the information we receive; we cannot be held responsible for outcomes arising from data that was not accurate or complete.
        </p>

        <SubHeading id="5.3" title="Government or Authority Rejections" />
        <p>
          For services involving certifications, registrations, funding, or legal filings, outcomes are determined by government departments, regulatory authorities, or financial institutions. Rejection or non-approval by an external authority does not entitle the client to a refund. Our service is the facilitation, preparation, and submission — not the guarantee of approval.
        </p>

        <SubHeading id="5.4" title="Change of Mind After Commencement" />
        <p>
          Once a service has commenced, a change of mind or business decision to not proceed is not a valid basis for a refund. Our team will have already invested time, effort, and resources into your project.
        </p>

        <SubHeading id="5.5" title="Partially Completed Work" />
        <p>
          Refunds are not applicable for the portion of work that has already been completed. If a service is partially delivered, any refund (where eligible) will apply only to the undelivered portion, calculated after considering effort, time, and costs already invested.
        </p>

        <SubHeading id="5.6" title="External or Third-Party Dependencies" />
        <p>
          If service timelines or outcomes are impacted by delays or failures on the part of third-party vendors, government portals, telecom systems, or other external platforms that are not within our control, refunds will not be applicable on that basis.
        </p>

        <SubHeading id="5.7" title="Digital & Intellectual Deliverables Once Delivered" />
        <p>
          Reports, strategies, plans, research outputs, designs, code, content, and any other intellectual or digital deliverables are non-refundable once they have been shared with the client. The value of such deliverables lies in the expertise invested in creating them.
        </p>

        {/* 6 ─ Third-Party & Government Fee Protection */}
        <SectionHeading n="6" title="Third-Party & Government Fee Protection" />
        <p>
          Many of our services involve payments made on your behalf to government departments, certification bodies, legal registrars, technology vendors, or other third parties. These costs are:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Non-refundable once disbursed, regardless of the outcome of the application or process</li>
          <li>Separate from our professional service fees</li>
          <li>Inclusive of government filing fees, stamp duties, registration charges, vendor licensing costs, platform fees, and applicable taxes</li>
        </ul>
        <p className="mt-3">
          We will always communicate third-party costs clearly in advance. Any such costs incurred will be reflected in your invoice and cannot be recovered through a refund request.
        </p>

        {/* 7 ─ How Refunds Are Calculated */}
        <SectionHeading n="7" title="How Refunds Are Calculated" />
        <p>When a refund is determined to be applicable following our review process, the refundable amount is arrived at transparently, taking into account:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>The proportion of work already completed at the time of the request</li>
          <li>Time and expertise invested by our team members, consultants, and specialists</li>
          <li>Third-party fees, vendor payments, government charges, and other direct costs incurred</li>
          <li>Administrative, processing, and coordination costs</li>
          <li>Any platform or payment gateway charges that are non-recoverable</li>
        </ul>
        <p className="mt-3">
          In practical terms: refunds, where applicable, represent the difference between the total fees paid and the value of work already performed and costs already incurred. We ensure this calculation is done transparently and shared with the client upon request.
        </p>
        <Callout>
          We do not apply arbitrary percentage caps. Every case is reviewed on its own merits, and the refundable amount reflects the actual state of the work at the time of the request.
        </Callout>

        {/* 8 ─ How to Raise a Refund Request */}
        <SectionHeading n="8" title="How to Raise a Refund Request" />
        <p>We have designed a simple, structured process to make refund requests easy and traceable:</p>

        <div className="mt-4 space-y-4">
          <StepBox num="1" title="Send a Written Request">
            <p>Email your refund request to our dedicated support address. Please include the following:</p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Your full name and registered business name (if applicable)</li>
              <li>Invoice number and payment reference or transaction ID</li>
              <li>Date of payment and amount paid</li>
              <li>The specific service for which the refund is being requested</li>
              <li>A clear description of the reason for the request</li>
              <li>Any supporting documents or evidence you wish to include</li>
            </ul>
          </StepBox>

          <StepBox num="2" title="Acknowledgement">
            <p>We will acknowledge receipt of your request within 2 business days and assign a reference number for tracking.</p>
          </StepBox>

          <StepBox num="3" title="Internal Review">
            <p>Our team will review your request, assess the status of the service, and evaluate eligibility based on this policy. We may reach out for additional information during this stage.</p>
          </StepBox>

          <StepBox num="4" title="Resolution Communication">
            <p>We will communicate the outcome of our review, along with a clear explanation of the decision, within the timelines specified in Section 9.</p>
          </StepBox>
        </div>

        {/* 9 ─ Request Windows & Processing Timelines */}
        <SectionHeading n="9" title="Request Windows & Processing Timelines" />
        <div className="overflow-x-auto my-4">
          <table className="w-full border-collapse text-[0.7rem]">
            <thead>
              <tr className="border-b-2 border-[#0D1B3E] text-left">
                <th className="pb-2 pr-3 font-semibold text-[#0D1B3E]">Stage</th>
                <th className="pb-2 font-semibold text-[#0D1B3E]">Timeline</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr><td className="py-2 pr-3 font-medium">Request Submission Window</td><td className="py-2">Within 30 calendar days from the date of payment or service delivery, whichever is earlier</td></tr>
              <tr><td className="py-2 pr-3 font-medium">Acknowledgement of Request</td><td className="py-2">Within 2 business days of receiving the request</td></tr>
              <tr><td className="py-2 pr-3 font-medium">Review &amp; Evaluation Period</td><td className="py-2">7 to 14 business days (complex cases may take up to 21 days)</td></tr>
              <tr><td className="py-2 pr-3 font-medium">Refund Processing (if approved)</td><td className="py-2">7 to 10 business days from the date of approval, to the original payment source</td></tr>
              <tr><td className="py-2 pr-3 font-medium">Service Credit Processing (if opted)</td><td className="py-2">Within 3 business days of approval</td></tr>
            </tbody>
          </table>
        </div>

        {/* 10 ─ Service Credit Option */}
        <SectionHeading n="10" title="Service Credit Option" />
        <p>
          As an alternative to a cash refund, we offer the option of Service Credits — a flexible and often more valuable path for clients who wish to continue their journey with us.
        </p>

        <SubHeading id="" title="What Are Service Credits?" />
        <p>
          Service Credits are equivalent in value to the approved refund amount and can be applied toward any of our services within the validity period. They are not transferable to third parties.
        </p>

        <SubHeading id="" title="Why Consider Service Credits?" />
        <ul className="list-disc pl-5 space-y-1">
          <li>They are typically processed faster than cash refunds</li>
          <li>They carry the full approved value — no deductions for processing fees</li>
          <li>They can be used across different service categories</li>
          <li>They give you flexibility to revisit or expand your engagement with us</li>
        </ul>

        <SubHeading id="" title="Validity" />
        <p>
          Service Credits are valid for <strong>12 months</strong> from the date of issuance. If not utilised within this period, they will lapse without extension.
        </p>
        <Callout>
          Clients opting for Service Credits will be asked to confirm their preference in writing. Once Service Credits are issued, the option to convert back to a cash refund is not available.
        </Callout>

        {/* 11 ─ Service-Specific Provisions */}
        <SectionHeading n="11" title="Service-Specific Provisions" />
        <p>Given the diverse nature of our services, the following specific conditions apply in addition to the general policy above:</p>

        <SubHeading id="11.1" title="Funding & Loan Facilitation Services" />
        <p>
          Our role in funding and loan services is to prepare, coordinate, and facilitate applications to NBFCs, banks, and government authorities. Loan approval, sanction, disbursement, or rejection is entirely at the discretion of the lending or approving institution.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Our fee covers the advisory, documentation, coordination, and submission effort</li>
          <li>Non-approval or rejection by the lender or government authority does not constitute a failure of our service</li>
          <li>Refunds are not applicable once the application process has commenced</li>
        </ul>

        <SubHeading id="11.2" title="Certifications & Registrations (ISO, Startup India, Udyam, GeM, FCRA, 12A/80G, 80 IAC)" />
        <p>
          Certification and registration timelines are governed by the respective authorities. We facilitate the process with accuracy and diligence, but cannot control processing speeds or approval decisions.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Government portal delays, system downtime, or authority-side delays do not entitle the client to a refund</li>
          <li>If an application is rejected due to ineligibility or authority decision, our professional fee for preparation and submission remains non-refundable</li>
          <li>Government filing fees paid on your behalf are non-refundable regardless of outcome</li>
        </ul>

        <SubHeading id="11.3" title="Legal Services" />
        <p>Legal work, once commenced, requires investment of time, research, and expertise that cannot be recovered.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Consultation fees are non-refundable once the session has taken place</li>
          <li>For ongoing or project-based legal work, refunds will only be considered for clearly unstarted portions of the scope</li>
          <li>Drafted documents, contracts, or compliance reports are non-refundable once delivered</li>
        </ul>

        <SubHeading id="11.4" title="Marketing Services" />
        <p>
          Marketing outcomes — such as leads generated, reach, conversions, or brand growth — depend on a wide range of market factors, audience behaviour, platform algorithms, and campaign dynamics. We do not offer guarantees of specific performance results.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Campaign strategy, creative work, and execution fees are non-refundable once work has commenced</li>
          <li>Ad spend paid to third-party platforms (Google, Meta, LinkedIn, etc.) is non-refundable once disbursed</li>
          <li>Disputes regarding marketing performance will be addressed through campaign optimisation, not refunds</li>
        </ul>

        <SubHeading id="11.5" title="IT & Technology Services" />
        <p>
          All technology projects are delivered based on a defined scope of work agreed upon at project initiation. Changes in scope, business direction, or technology preferences post-commencement are handled through a formal Change Request process and may involve additional charges.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Development work completed as per agreed specifications is non-refundable</li>
          <li>Custom software, applications, or systems delivered to the client are non-refundable</li>
          <li>For ongoing retainer or SaaS-based services, the unused portion of a billing cycle (if cancelled mid-period) may be eligible for credit but not a cash refund</li>
          <li>Hosting, licensing, domain, and third-party API costs are non-refundable once procured</li>
        </ul>

        <SubHeading id="11.6" title="Business Consultancy" />
        <p>
          Consultation is considered consumed at the point of delivery. When our consultants invest time, knowledge, and strategy in a meeting, session, or report, that value has been provided irrespective of the client&apos;s subsequent decisions.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Advisory sessions, strategy reviews, and business assessments are non-refundable once conducted</li>
          <li>Deliverables such as reports, plans, and assessments are non-refundable once submitted</li>
        </ul>

        <SubHeading id="11.7" title="Client Responsibility Clause" />
        <p>
          The accuracy of all information provided to us — including KYC documents, business details, financial data, and compliance history — is the sole responsibility of the client. Any consequences arising from inaccurate, outdated, or misleading information, including service failure, rejection, or legal liability, will be borne by the client. We will not issue refunds in such circumstances.
        </p>

        {/* 12 ─ Force Majeure */}
        <SectionHeading n="12" title="Force Majeure" />
        <p>
          Neither the company nor the client shall be held liable for delays or failure in performance of any service obligation resulting from circumstances beyond reasonable control, including but not limited to:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Natural disasters, floods, earthquakes, or other acts of nature</li>
          <li>Government orders, regulatory changes, or policy shifts</li>
          <li>National emergencies, pandemics, or public health crises</li>
          <li>Internet or infrastructure outages not within our control</li>
          <li>Strikes, lockouts, or civil disturbances</li>
        </ul>
        <p className="mt-3">
          In such cases, we will make reasonable efforts to resume services as soon as possible and will communicate proactively with affected clients. Refunds will not be issued for delays or disruptions caused by force majeure events, though service credit or timeline extensions will be considered on a case-by-case basis.
        </p>

        {/* 13 ─ Limitation of Liability */}
        <SectionHeading n="13" title="Limitation of Liability" />
        <p>
          Our liability in connection with any service, to the fullest extent permitted by law, is limited to the professional fee paid by the client for that specific service. We shall not be liable for:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Indirect, incidental, or consequential losses</li>
          <li>Loss of business opportunity, anticipated profits, or revenue</li>
          <li>Losses arising from decisions made by government authorities, lenders, or regulators</li>
          <li>Losses arising from third-party platform failures</li>
          <li>Losses resulting from client-provided inaccurate information</li>
        </ul>

        {/* 14 ─ Dispute Resolution */}
        <SectionHeading n="14" title="Dispute Resolution" />
        <p>We are committed to resolving concerns amicably and constructively. If you are dissatisfied with the outcome of your refund request, here is the process we follow:</p>

        <div className="mt-4 space-y-4">
          <StepBox num="1" title="Internal Escalation">
            <p>Raise your concern in writing to our senior management team. We will personally review the matter and respond within 10 business days.</p>
          </StepBox>

          <StepBox num="2" title="Mediation">
            <p>If the dispute remains unresolved after internal escalation, both parties agree to attempt resolution through a mutually agreed mediator before pursuing legal proceedings.</p>
          </StepBox>

          <StepBox num="3" title="Arbitration / Legal Proceedings">
            <p>
              If mediation does not lead to a resolution, disputes shall be subject to arbitration under the Arbitration and Conciliation Act, 1996, with the seat of arbitration in <strong>Noida, India</strong>. The governing law shall be Indian law and jurisdiction shall vest in the courts of Noida, Uttar Pradesh.
            </p>
          </StepBox>
        </div>

        {/* 15 ─ Policy Updates & Revisions */}
        <SectionHeading n="15" title="Policy Updates & Revisions" />
        <p>
          We reserve the right to update or revise this Refund &amp; Cancellation Policy at any time to reflect changes in our services, legal requirements, or business practices. Updates will be effective immediately upon publication on our official website and communication channels.
        </p>
        <p>
          Clients are encouraged to review this policy periodically. Continued use of our services after any revision constitutes acceptance of the updated policy. For significant changes, we will make reasonable efforts to notify existing clients by email.
        </p>

        {/* 16 ─ Contact Information */}
        <SectionHeading n="16" title="Contact Information" />
        <p>For refund requests, payment disputes, or any queries related to this policy, please reach out to us:</p>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-collapse text-[0.7rem]">
            <tbody className="divide-y divide-gray-200">
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Email (Refund Requests)</td><td className="py-2"><a href="mailto:support@farsight.co.in" className="text-[#1565C0] underline">support@farsight.co.in</a></td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Email (General Support)</td><td className="py-2"><a href="mailto:info@farsight.co.in" className="text-[#1565C0] underline">info@farsight.co.in</a></td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Phone / WhatsApp</td><td className="py-2"><a href="tel:+919821605930" className="text-[#1565C0] underline">+91 9821605930</a> (Mon–Sat, 10 AM – 6 PM)</td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Registered Address</td><td className="py-2">Farsight Solutions LLP, ILD Business Center, H-207, Sector 63, Noida, Uttar Pradesh – 201009, India</td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Website</td><td className="py-2"><a href="https://www.farsight.co.in" className="text-[#1565C0] underline" target="_blank" rel="noopener noreferrer">www.farsight.co.in</a></td></tr>
            </tbody>
          </table>
        </div>

        {/* ── Closing ── */}
        <div className="mt-12 rounded-lg border border-[#C95A0E]/20 bg-[#FDF8F4] px-6 py-6 text-center">
          <p className="text-[0.85rem] font-semibold text-[#0D1B3E]">
            We appreciate your trust in us
          </p>
          <p className="mt-2 text-[0.72rem] text-[#5A3E2B]">
            This policy is designed to be fair, clear, and transparent — protecting both you and us as partners in your growth journey.
          </p>
        </div>

        <p className="mt-8 text-center text-[0.65rem] text-gray-400">
          © 2024 Farsight Solutions LLP. All Rights Reserved.
        </p>
      </article>
    </main>
  );
}
